import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/app/Logo";
import { Check, MailX } from "lucide-react";

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const tokenFromQuery = params.get("token") || "";
  const emailFromQuery = params.get("email") || "";
  const actionFromQuery = (params.get("action") || "").toLowerCase();  // "pause" -> soft 30-day mute
  const [email, setEmail] = useState(emailFromQuery);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Soft-success: server accepted a 30-day pause (via /api/pause-emails).
  const [pausedUntil, setPausedUntil] = useState("");
  // Second success state — user typed their email without a token, so the
  // server sent them a confirmation link instead of unsubscribing directly.
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [error, setError] = useState("");

  // If both email + token are in the URL, auto-execute the action.
  useEffect(() => {
    if (emailFromQuery && tokenFromQuery && !done && !submitting && !pausedUntil) {
      if (actionFromQuery === "pause") {
        doPause(emailFromQuery, tokenFromQuery);
      } else {
        doUnsubscribe(emailFromQuery, tokenFromQuery);
      }
    }
    // Intentional single-run on mount — see SEC-003 notes.
    // eslint-disable-next-line
  }, []);

  const doUnsubscribe = async (targetEmail, token) => {
    setError("");
    setConfirmationSent(false);
    setSubmitting(true);
    try {
      const { data } = await api.post("/unsubscribe", { email: targetEmail, token: token || undefined });
      if (data?.unsubscribed) {
        setDone(true);
      } else {
        // Server didn't unsubscribe — it either dispatched a confirmation
        // email (email is on our list) or silently ignored (email isn't).
        // Show the same "check your email" message either way so we don't
        // leak membership.
        setConfirmationSent(true);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not unsubscribe");
    } finally {
      setSubmitting(false);
    }
  };

  const doPause = async (targetEmail, token) => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/pause-emails", { email: targetEmail, token });
      if (data?.paused && data?.paused_until) {
        setPausedUntil(data.paused_until);
      } else {
        setError("This pause link has expired. Please request a fresh one from an email footer.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not pause emails");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (!email.trim()) return;
    doUnsubscribe(email.trim(), tokenFromQuery);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="unsubscribe-page">
      <Card className="w-full max-w-md border-border shadow-none">
        <CardContent className="p-8">
          <div className="flex items-center justify-center mb-8">
            <Link to="/"><Logo size={72} textClassName="text-2xl" /></Link>
          </div>

          {done ? (
            <div className="text-center" data-testid="unsubscribe-success">
              <div className="h-12 w-12 mx-auto rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-4">
                <Check size={22} />
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-tight">You&apos;re unsubscribed</h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-prose mx-auto">
                We won&apos;t send any more newsletters to <strong>{email}</strong>. Transactional emails
                (password resets, reminders you set up yourself) will still arrive.
              </p>
              <Link to="/" className="inline-block mt-6">
                <Button variant="outline">Back to home</Button>
              </Link>
            </div>
          ) : pausedUntil ? (
            <div className="text-center" data-testid="pause-success">
              <div className="h-12 w-12 mx-auto rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-4">
                <Check size={22} />
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-tight">Emails paused</h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-prose mx-auto">
                We&apos;ll stop all Your Dog Days emails to <strong>{email}</strong> for the next 30 days,
                then quietly resume on <strong data-testid="pause-until-date">{new Date(pausedUntil).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</strong>.
              </p>
              <p className="text-xs text-muted-foreground mt-4 max-w-prose mx-auto">
                Change your mind sooner? Log in and hit &quot;Resume emails&quot; on Settings, or just wait — everything comes back automatically.
              </p>
              <Link to="/" className="inline-block mt-6">
                <Button variant="outline">Back to home</Button>
              </Link>
            </div>
          ) : confirmationSent ? (
            <div className="text-center" data-testid="unsubscribe-confirmation-sent">
              <div className="h-12 w-12 mx-auto rounded-md bg-secondary text-secondary-foreground flex items-center justify-center mb-4">
                <MailX size={22} />
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-tight">Check your email</h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-prose mx-auto">
                If <strong>{email}</strong> is on our list, we just sent it a confirmation link.
                Click the button in that email to complete the unsubscribe &mdash; nothing will change until you do.
              </p>
              <p className="text-xs text-muted-foreground mt-4 max-w-prose mx-auto">
                Don&apos;t see it? Check spam, or wait 5 minutes before trying again.
              </p>
              <Link to="/" className="inline-block mt-6">
                <Button variant="outline">Back to home</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                  <MailX size={20} />
                </div>
                <div>
                  <h1 className="font-display text-2xl font-extrabold tracking-tight">Unsubscribe</h1>
                  <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mt-1">Newsletter opt-out</p>
                </div>
              </div>
              <p className="text-sm text-foreground/80 mb-5">
                Enter your email and we&apos;ll remove you from the Your Dog Days newsletter list.
                Transactional emails (password resets, reminders) will still arrive.
              </p>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="unsubscribe-form">
                <div>
                  <Label htmlFor="u-email">Email</Label>
                  <Input
                    id="u-email"
                    data-testid="unsubscribe-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5"
                    autoFocus
                  />
                </div>
                {error ? <p className="text-sm text-destructive" data-testid="unsubscribe-error">{error}</p> : null}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="unsubscribe-submit"
                >
                  {submitting ? "Unsubscribing…" : "Unsubscribe me"}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-6 text-center">
                Changed your mind? <Link to="/" className="underline underline-offset-4">Back to home</Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
